/**
 * Browser notification + audio chime for export-completion events.
 *
 * The existing toast covers the "tab is focused" case; these cover the
 * "tab is backgrounded / user has switched to another app" case, which is
 * common during multi-minute video exports.
 *
 * All functions are permission- and feature-safe: if the browser doesn't
 * support Notifications (e.g. iOS Safari) or the user has denied permission,
 * they silently no-op — the caller's existing toast/visual feedback still
 * fires.
 */

let sharedAudioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (sharedAudioCtx) return sharedAudioCtx;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    sharedAudioCtx = new Ctor();
    return sharedAudioCtx;
  } catch {
    return null;
  }
}

/**
 * Two-note ascending chime (C5 → E5, ~450ms total). Synthesized via Web
 * Audio API so we don't have to ship an audio asset.
 */
export function playCompletionChime(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Chrome + Safari suspend AudioContexts that were created before any user
  // gesture. Attempt to resume — if it fails (no gesture yet) we just skip;
  // the user will still see the notification / toast.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => undefined);
  }

  const now = ctx.currentTime;
  const notes = [523.25, 659.25]; // C5, E5
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain).connect(ctx.destination);
    const start = now + i * 0.18;
    const end = start + 0.3;
    osc.start(start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, end);
    osc.stop(end + 0.05);
  });
}

/**
 * Ask the browser for notification permission if we haven't yet. Returns
 * the final permission state. Safe to call multiple times — once the user
 * has answered (granted/denied), further calls are no-ops.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

interface ShowNotificationOptions {
  title: string;
  body: string;
  /** Tag lets a newer notification replace an older one instead of stacking. */
  tag?: string;
  /** Called when the user clicks the notification — use to focus the tab. */
  onClick?: () => void;
}

export function showExportCompleteNotification(opts: ShowNotificationOptions): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      icon: '/favicon.ico',
      tag: opts.tag ?? 'ivy-export-complete',
    });
    n.onclick = () => {
      window.focus();
      opts.onClick?.();
      n.close();
    };
  } catch {
    // Some browsers throw on `new Notification(...)` from non-SW contexts
    // (e.g. older mobile browsers). Silently ignore — toast + chime cover it.
  }
}
